import React, { useState, useRef, useEffect } from "react";
import api from "../services/api";
import { Sparkles, Mic, MicOff, Camera, Volume2, VolumeX, Send, X, Image as ImageIcon, Bot, User, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { jwtDecode } from "jwt-decode";
import { getToken } from "../services/auth";
import toast, { Toaster } from 'react-hot-toast';

export default function VoiceTutor() {
  const [userRole, setUserRole] = useState("student");
  const [input, setInput] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [muteSpeaker, setMuteSpeaker] = useState(false);
  const [stagedImage, setStagedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState([
    { role: "assistant", content: "Hey there, explorer! 🚀 I'm your Antigravity Learning Buddy! Tap the mic to speak, or tap the camera to show me an image! What are we learning today?" }
  ]);

  const fileInputRef = useRef(null);
  const chatBottomRef = useRef(null);
  const synth = window.speechSynthesis;
  const recognitionRef = useRef(null);

  useEffect(() => {
    try {
      const token = getToken();
      if (token) setUserRole(jwtDecode(token).role || "student");
    } catch (err) {}
  }, []);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation]);

  // Standard browser voice output
  const speakOutLoud = (text) => {
    if (muteSpeaker || !synth) return;
    synth.cancel(); // stop previous
    
    // Clean markdown for cleaner speaking
    const cleanText = text.replace(/[*#_`~>]/g, '').replace(/\[.*?\]/g, '');
    const utterance = new SpeechSynthesisUtterance(cleanText);
    
    // Try to find a friendly child/natural voice
    const voices = synth.getVoices();
    const selectedVoice = voices.find(v => v.name.includes("Google US English") || v.name.includes("Natural")) || voices[0];
    if (selectedVoice) utterance.voice = selectedVoice;
    
    utterance.pitch = 1.1; // Slightly child-friendly playful pitch
    utterance.rate = 1.0;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    synth.speak(utterance);
  };

  const stopSpeaking = () => {
    synth?.cancel();
    setIsSpeaking(false);
  };

  // Voice Recognition Control
  const toggleListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Voice input is not supported in this browser. Please try Chrome.");
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    stopSpeaking(); // Stop AI from speaking if user starts speaking
    const recognition = new SpeechRecognition();
    recognition.continuous = false; // Just grab a single query
    recognition.lang = "en-US";
    recognition.interimResults = true;

    recognition.onstart = () => setIsListening(true);
    recognition.onerror = (e) => {
      console.error(e);
      setIsListening(false);
      toast.error("Microphone error. Please verify permissions.");
    };
    recognition.onend = () => setIsListening(false);

    recognition.onresult = (event) => {
      const resultText = event.results[0][0].transcript;
      setInput(resultText);
      
      if (event.results[0].isFinal) {
        // Automatically trigger submission for seamless conversation!
        setTimeout(() => handleSubmitMessage(resultText), 800);
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStagedImage({
      file: file,
      url: URL.createObjectURL(file)
    });
    toast.success("Problem image loaded! Ready to solve 📸");
  };

  const handleSubmitMessage = async (forcedInput = null) => {
    const activeInput = forcedInput !== null ? forcedInput : input;
    if ((!activeInput.trim() && !stagedImage) || loading) return;

    const userQuery = activeInput;
    const userImg = stagedImage;
    
    setInput("");
    setStagedImage(null);
    stopSpeaking();

    const updatedHistory = [...conversation, { role: "user", content: userQuery || "[Photo Problem Attached]", image: userImg?.url }];
    setConversation(updatedHistory);
    setLoading(true);

    try {
      let base64Payload = null;
      if (userImg?.file) {
        base64Payload = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(userImg.file);
          reader.onload = () => resolve(reader.result);
        });
      }

      const response = await api.post("/ai/chat", {
        messages: updatedHistory.map(c => ({ role: c.role, content: c.content })),
        image_base64: base64Payload
      });

      const aiReply = response.data.response || "Oops, my circuits crossed! Can you say that again?";
      setConversation([...updatedHistory, { role: "assistant", content: aiReply }]);
      
      // Physically speak the result out loud!
      speakOutLoud(aiReply);

    } catch (err) {
      setConversation([...updatedHistory, { role: "assistant", content: "Oops! I'm having trouble reaching my cloud vector base. Give me one second!" }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-[#F4F3FF] dark:bg-slate-950 overflow-hidden relative font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      
      <main className="flex-1 flex flex-col h-full relative z-10 min-w-0">
        {/* Visual ambient aura */}
        <div className="absolute top-[-10%] right-[-10%] w-[40vw] h-[40vw] bg-indigo-400/10 rounded-full blur-[120px] pointer-events-none animate-pulse" />
        <div className="absolute bottom-[-10%] left-[10%] w-[30vw] h-[30vw] bg-purple-400/10 rounded-full blur-[120px] pointer-events-none" />

        {/* Floating Controls Header */}
        <header className="h-20 px-8 flex items-center justify-between border-b-4 border-white dark:border-slate-900 bg-white/50 dark:bg-slate-900/50 backdrop-blur-md relative z-20 flex-shrink-0">
          <div className="flex items-center gap-3">
            <motion.div 
              animate={{ scale: isSpeaking ? [1, 1.1, 1] : 1 }}
              transition={{ repeat: Infinity, duration: 1 }}
              className="w-10 h-10 bg-indigo-500 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-500/20 text-xl font-bold"
            >
              🤖
            </motion.div>
            <div>
              <h1 className="text-lg font-black text-slate-900 dark:text-white tracking-tight leading-tight flex items-center gap-2">
                Kids AI Voice Tutor <Sparkles className="text-amber-500 animate-spin" size={14} style={{ animationDuration: '5s' }} />
              </h1>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 font-black tracking-widest uppercase">Vision & Speech Active</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const newMute = !muteSpeaker;
                setMuteSpeaker(newMute);
                if (newMute) stopSpeaking();
                toast.success(newMute ? "AI Audio Muted" : "AI Audio Active");
              }}
              className={`p-3 rounded-xl border-b-4 font-black shadow-md transition-all flex items-center gap-2 ${
                muteSpeaker 
                  ? "bg-slate-200 border-slate-300 text-slate-600" 
                  : "bg-indigo-50 border-indigo-200 text-indigo-600"
              }`}
            >
              {muteSpeaker ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </div>
        </header>

        {/* Visual Animation Hub & Audio Wave Form */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 space-y-8 overflow-y-auto relative">
          <div className="w-full max-w-3xl flex-1 flex flex-col gap-6 no-scrollbar overflow-y-auto p-4">
            <AnimatePresence initial={false}>
              {conversation.map((msg, idx) => {
                const isAI = msg.role === "assistant";
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    className={`flex gap-4 max-w-[85%] ${isAI ? "mr-auto" : "ml-auto flex-row-reverse"}`}
                  >
                    <div className={`w-10 h-10 rounded-2xl flex-shrink-0 flex items-center justify-center shadow-md ${
                      isAI ? "bg-gradient-to-br from-indigo-500 to-purple-500 text-white" : "bg-white text-slate-600 border-b-4 border-slate-200"
                    }`}>
                      {isAI ? <Bot size={20} /> : <User size={20} />}
                    </div>
                    
                    <div className="space-y-1">
                      <div className={`px-6 py-4 rounded-[2rem] border-b-4 shadow-md text-[15px] font-bold leading-relaxed relative ${
                        isAI 
                          ? "bg-white border-indigo-100 dark:bg-slate-900 dark:border-slate-800 text-slate-800 dark:text-slate-200 rounded-tl-none" 
                          : "bg-indigo-600 border-indigo-800 text-white rounded-tr-none"
                      }`}>
                        {msg.image && (
                          <div className="mb-3 max-w-xs overflow-hidden rounded-[1.5rem] border-2 border-black/10 relative">
                            <img src={msg.image} alt="Problem staged" className="w-full object-contain" />
                          </div>
                        )}
                        <p>{msg.content}</p>
                      </div>
                      <p className={`text-[9px] font-black text-slate-400 uppercase tracking-widest px-2 ${!isAI && "text-right"}`}>
                        {isAI ? "LMS AI Buddy" : "You"}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {loading && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3 px-14">
                <Loader2 size={16} className="animate-spin text-indigo-600" />
                <span className="text-sm italic font-black text-indigo-600">Processing Brain Matrix...</span>
              </motion.div>
            )}
            <div ref={chatBottomRef} className="h-4" />
          </div>

          {/* Interactive Big Sphere Visualizer */}
          <div className="w-full max-w-2xl bg-white/60 backdrop-blur-md dark:bg-slate-900/60 p-8 rounded-[3rem] border-b-8 border-slate-200 dark:border-slate-800 shadow-xl flex flex-col items-center space-y-6 flex-shrink-0 relative">
            
            {/* Audio Visualizer Rings */}
            <div className="relative flex items-center justify-center w-32 h-32">
              <AnimatePresence>
                {(isSpeaking || isListening) && (
                  <>
                    <motion.div
                      initial={{ scale: 1, opacity: 0.5 }}
                      animate={{ scale: 1.8, opacity: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ repeat: Infinity, duration: 1.5 }}
                      className={`absolute inset-0 rounded-full ${isListening ? 'bg-red-400/40' : 'bg-indigo-400/40'}`}
                    />
                    <motion.div
                      initial={{ scale: 1, opacity: 0.6 }}
                      animate={{ scale: 1.5, opacity: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ repeat: Infinity, duration: 1.2, delay: 0.4 }}
                      className={`absolute inset-0 rounded-full ${isListening ? 'bg-red-500/30' : 'bg-indigo-500/30'}`}
                    />
                  </>
                )}
              </AnimatePresence>

              {/* Core Interactive Mic Node */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleListening}
                className={`w-24 h-24 rounded-full flex items-center justify-center text-white shadow-2xl border-b-8 active:translate-y-1 active:border-b-0 transition-all z-10 ${
                  isListening 
                    ? "bg-red-500 border-red-700 hover:bg-red-600 shadow-red-500/30" 
                    : "bg-indigo-600 border-indigo-800 hover:bg-indigo-700 shadow-indigo-600/30"
                }`}
              >
                {isListening ? <MicOff size={36} className="animate-pulse" /> : <Mic size={36} />}
              </motion.button>
            </div>

            <p className="text-sm font-black text-center tracking-wide text-slate-700 dark:text-slate-200">
              {isListening 
                ? "🌟 Listening carefully... keep speaking!" 
                : isSpeaking 
                  ? "🗣️ AI Buddy speaking aloud..." 
                  : "👋 Tap the mic above to speak to your Tutor!"}
            </p>

            {/* Input Tray with Text / Camera fallbacks */}
            <div className="w-full flex items-center gap-3 bg-[#F4F3FF] dark:bg-slate-800/50 p-2 rounded-[2rem] border border-slate-100 dark:border-slate-700 relative">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                className="hidden"
                onChange={handleImageSelect}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-3.5 bg-white dark:bg-slate-800 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-2xl border-b-4 border-slate-200 dark:border-slate-900 transition-all shrink-0"
                title="Capture Photo Problem"
              >
                <Camera size={20} />
              </button>

              <div className="relative flex-1">
                {stagedImage && (
                  <div className="absolute -top-24 left-0 bg-white p-2 rounded-xl border-2 border-indigo-200 shadow-md flex items-center gap-2 animate-bounce">
                    <img src={stagedImage.url} alt="Staged" className="w-12 h-12 object-cover rounded-lg" />
                    <button onClick={() => setStagedImage(null)} className="p-1 bg-red-500 text-white rounded-full"><X size={12}/></button>
                  </div>
                )}
                <input
                  type="text"
                  placeholder="Or type your question here..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmitMessage()}
                  className="w-full bg-transparent font-bold text-sm text-slate-800 dark:text-slate-100 px-3 focus:outline-none py-3"
                />
              </div>

              <button
                onClick={() => handleSubmitMessage()}
                disabled={(!input.trim() && !stagedImage) || loading}
                className="p-3.5 bg-indigo-600 text-white rounded-2xl border-b-4 border-indigo-800 hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 active:translate-y-1 active:border-b-0 disabled:opacity-50 transition-all shrink-0"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
